
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { MappedLeadData, CampaignMetadata } from '../types';
import { Sidebar } from './Sidebar';
import { getAppwriteStorageUrl } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import { useLeadDetail } from '../hooks/useLeadDetail';
import { useCampaigns } from '../hooks/useCampaigns';
import { getPreferredStatus, isLeadEnrichedForISR } from '../utils/statusMapping';
import { fetchCampaignMetadata } from '../utils/data';
import { useStageReveal } from './UniBox';
import { SkeletonSection } from './atoms/Skeleton';

// Tab Imports
import { OverviewTab } from './Tabs/OverviewTab';
import { InteractionsTab } from './Tabs/InteractionsTab';
import { ISRBriefingTab } from './Tabs/ISRBriefingTab';
// ISRBriefingTab is no longer rendered in this modal — the ISR workspace
// drawer owns the human-preparation surface. The import is kept for the
// ISR gating check (isLeadEnrichedForISR) used by the "Full lead" flow.
import { PersonTab } from './Tabs/PersonTab';
import { CompanyTab } from './Tabs/CompanyTab';
import { PainsChallengesTab } from './Tabs/PainsChallengesTab';
import { OpportunitiesTab } from './Tabs/OpportunitiesTab';
import { SignalsTab } from './Tabs/SignalsTab';
import { NetworkTab } from './Tabs/NetworkTab';
import { ProfileIntelTab } from './Tabs/ProfileIntelTab';
import { OutreachTab } from './Tabs/OutreachTab';
import { LEAD_DETAIL_TABS } from './Tabs/leadDetailTabs';
// import { KnowledgeBoardTab } from './Tabs/KnowledgeBoardTab'; // Commented out - component not yet implemented

interface LeadReportModalProps {
  lead: MappedLeadData | null;
  onClose: () => void;
  previewMode?: boolean;
  /** Authoritative campaign ID from the route (preferred over lead.campaignId). */
  campaignId?: string;
}

/**
 * Resolve a usable campaign ID.
 * Prefers the route-provided id (ground truth), then falls back to the lead's id.
 * Rejects the sentinel value "unknown" that the N8N transformer emits when
 * lvl2.campaign_id is missing — that value never matches a real campaign and
 * would silently break every campaign-dependent view (e.g. ISR Briefing).
 */
const resolveCampaignId = (routeCampaignId?: string, leadCampaignId?: string): string => {
  const candidates = [routeCampaignId, leadCampaignId ? String(leadCampaignId) : ''];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const trimmed = candidate.trim();
    if (trimmed && trimmed.toLowerCase() !== 'unknown') return trimmed;
  }
  return '';
};

export const LeadReportModal: React.FC<LeadReportModalProps> = ({ lead, onClose, previewMode = false, campaignId: routeCampaignId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { currentUser, targetEmail: impersonatedEmail } = useAuth();
  const isReplacementLead = Boolean(lead?.replaces);
  const effectiveUserEmail = impersonatedEmail || currentUser?.email || '';
  const leadId = lead?.id ? String(lead.id) : '';
  const campaignId = resolveCampaignId(routeCampaignId, lead?.campaignId);

  // GSAP "liquid reveal" loading FX — fires on the tab content region only.
  // The header, sidebar, and tab bar render immediately from the passed `lead`
  // (already-known data); only the tab content waits for `liveLead` and so
  // only it blurs in. Keyed on lead id so opening a different lead re-fires.
  const stageRef = useStageReveal(String(lead?.id ?? 'preview'), { revealOnFirstMount: true });

  const { data: liveLead } = useLeadDetail(
    campaignId,
    leadId,
    previewMode ? '' : effectiveUserEmail
  );
  const replacedLeadId = lead?.replaces ? String(lead.replaces) : '';
  const { data: replacedLead, isLoading: isLoadingReplacedLead } = useLeadDetail(
    lead?.campaignId || '',
    replacedLeadId,
    previewMode ? '' : effectiveUserEmail
  );

  // True while the live lead record hydrates. The header/sidebar/tab bar
  // render from the passed `lead` (known data); only the tab content shows
  // a skeleton until `liveLead` arrives.
  const hydrating = !previewMode && !liveLead;

  // Single source of truth for campaigns is the cached useCampaigns() list
  // (keyed by the effective/impersonated email). Resolve the campaign by ID from
  // that cache first; only fall back to a direct fetch if it isn't cached. This
  // avoids a fresh full campaign_metrics re-fetch per lead open — which previously
  // timed out (Appwrite 30s limit) and dropped the whole campaign object, breaking
  // every campaign-dependent view (e.g. ISR Briefing "Campaign context missing").
  const { data: cachedCampaigns } = useCampaigns(
    previewMode ? '' : (effectiveUserEmail || undefined)
  );
  const cachedCampaign = cachedCampaigns?.find(
    (c) => c.id === campaignId || (c.id && campaignId && c.id.toLowerCase() === campaignId.toLowerCase())
  );
  const { data: campaignMetadata } = useQuery({
    queryKey: ['campaign-metadata', campaignId, effectiveUserEmail],
    queryFn: () => fetchCampaignMetadata(campaignId),
    staleTime: 60 * 1000,
    enabled: !!effectiveUserEmail && !!campaignId && !previewMode && !cachedCampaign,
  });

  const resolvedCampaignMetadata: CampaignMetadata | undefined =
    cachedCampaign || campaignMetadata;

  // Reset tab when lead changes
  useEffect(() => {
    if (lead) {
      setActiveTab('overview');
    }
  }, [lead]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (lead) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [lead, onClose]);

  if (!lead) return null;

  const displayLead = liveLead
    ? {
        ...liveLead,
        status: getPreferredStatus(lead.status, liveLead.status) || liveLead.status,
      }
    : lead;

  const replacementReason = displayLead.replaces_reason?.trim();
  const replacedLeadName = replacedLead?.leadName?.trim() || `Lead #${displayLead.replaces}`;
  const replacedLeadRole = replacedLead?.leadRole?.trim();
  const replacedLeadCompany = replacedLead?.companyName?.trim();

  const renderTabContent = (data: MappedLeadData) => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={data} />;
      case 'isrbriefing':
        // ISR Briefing tab removed from this modal — the ISR workspace drawer
        // owns the human-preparation surface now. This case is kept as a
        // fallback that redirects to Interactions.
        return <InteractionsTab data={data} density="compact" />;
      // case 'knowledgeboard':
      //   return <KnowledgeBoardTab data={data} />; // Commented out - component not yet implemented
      case 'interactions':
        return <InteractionsTab data={data} density="compact" />;
      case 'person':
        return <PersonTab data={data} />;
      case 'company':
        return <CompanyTab data={data} />;
      case 'pains':
        return <PainsChallengesTab data={data} />;
      case 'opportunities':
        return <OpportunitiesTab data={data} />;
      case 'signals':
        return <SignalsTab data={data} />;
      case 'keypeople':
        return <NetworkTab data={data} />;
      case 'profileintel':
        return <ProfileIntelTab data={data} />;
      case 'outreach':
        return <OutreachTab data={data} />;
      default:
        return (
          <div className="flex flex-col items-center justify-center h-64 bg-[#121212] border border-white/5 rounded-3xl" role="status">
            <div className="text-[#808080] mb-2">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="text-[#A0A0A0] font-medium">Feature available in full version</div>
          </div>
        );
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10010] flex items-center justify-center p-2 sm:p-3 md:p-4 lg:p-5">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[rgba(3,6,12,0.82)] backdrop-blur-[18px]"
        onClick={onClose}
        style={{ backdropFilter: 'blur(18px)' }}
      ></div>

      {/* Modal Content — the stageRef wraps the whole panel so the blur-in
          plays once on open, but only the tab content region carries
          data-view-block (the area that waits for liveLead). */}
      <div
        ref={stageRef}
        className="relative flex h-[95vh] max-h-[95vh] w-full max-w-[1640px] flex-col overflow-hidden rounded-[24px] border border-white/8 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.06),transparent_24%),radial-gradient(circle_at_top_right,rgba(99,102,241,0.06),transparent_26%),linear-gradient(180deg,rgba(14,17,24,0.98),rgba(7,9,14,0.99))] shadow-[0_32px_90px_rgba(0,0,0,0.5)]"
        style={{ boxShadow: '0 32px 90px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)' }}
      >
        {/* Header — renders immediately from the passed lead (known data) */}
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] px-4 py-2 sm:px-5">
          <div className="flex min-w-0 items-center gap-2.5">
            {/* Mobile Sidebar Toggle */}
            <button
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-white transition-colors hover:bg-white/[0.08] md:hidden"
              aria-label="Toggle sidebar"
              aria-expanded={isMobileSidebarOpen}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
              <img src={getAppwriteStorageUrl('6940e3f8003c17bb7fc9', '6940e6690003b2d31294')}
                   alt="Brainiacs AI Agency Logo"
                   className="h-5 w-5 object-contain" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <h2 className="min-w-0 truncate text-base font-black leading-5 tracking-[-0.02em] text-white sm:text-lg">{displayLead.leadName}</h2>
                {previewMode && (
                  <span className="inline-flex items-center rounded-md border border-amber-400/30 bg-amber-400/12 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-amber-100">
                    Demo Preview
                  </span>
                )}
                {isReplacementLead && (
                  <div className="group relative">
                    <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-emerald-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                      Replaces Lead #{displayLead.replaces}
                    </span>

                    <div className="pointer-events-none absolute left-0 top-full z-30 mt-3 hidden w-[320px] rounded-[22px] border border-emerald-500/20 bg-[#0B1110]/96 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl group-hover:block">
                      <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
                          Replaced Lead
                        </div>
                        <div className="mt-1 truncate text-sm font-semibold text-white">{replacedLeadName}</div>
                        {isLoadingReplacedLead ? (
                          <div className="mt-0.5 text-xs text-gray-400">Loading replaced lead details...</div>
                        ) : (
                          <>
                            {replacedLeadRole && (
                              <div className="mt-0.5 text-xs text-gray-300">{replacedLeadRole}</div>
                            )}
                            {replacedLeadCompany && (
                              <div className="mt-0.5 text-[11px] text-gray-400">{replacedLeadCompany}</div>
                            )}
                          </>
                        )}
                      </div>

                      <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
                          Why This Lead Replaced #{displayLead.replaces}
                        </div>
                        <p className="mt-2 text-xs leading-5 text-gray-200">
                          {replacementReason || 'This lead was identified during enrichment as the stronger contact for this campaign.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.13em] text-[#8E98B5]">
                <span className="truncate">{displayLead.companyName}</span>
                {displayLead.leadRole && (
                  <>
                    <span className="h-1 w-1 shrink-0 rounded-full bg-white/20"></span>
                    <span className="truncate">{displayLead.leadRole}</span>
                  </>
                )}
              </div>
              {previewMode && (
                <p className="mt-0.5 text-[10px] leading-4 text-amber-200">Sample lead data. Actions are disabled.</p>
              )}
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-white transition-colors hover:bg-white/[0.08]"
            aria-label="Close modal (Escape key)"
            title="Close (Esc)"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex relative">
          {/* Mobile Sidebar Overlay */}
          {isMobileSidebarOpen && (
            <div
              className="md:hidden absolute inset-0 bg-black/60 z-10"
              onClick={() => setIsMobileSidebarOpen(false)}
              aria-label="Close sidebar overlay"
            />
          )}

          <div className="flex w-full h-full overflow-hidden">
            {/* Left Sidebar - renders immediately from the passed lead */}
            <div className={`
              w-[300px] border-r border-white/8 flex-shrink-0 overflow-hidden bg-[linear-gradient(180deg,rgba(5,7,11,0.96),rgba(7,10,15,0.98))]
              transition-transform duration-300 ease-in-out
              md:relative md:translate-x-0
              ${isMobileSidebarOpen ? 'absolute left-0 top-0 h-full z-20 translate-x-0' : 'absolute left-0 top-0 h-full -translate-x-full md:translate-x-0'}
            `}>
              <div className="h-full overflow-y-auto px-4 py-5 scrollbar-hide">
                <Sidebar data={displayLead} previewMode={previewMode} />
              </div>
            </div>

            {/* Right Content */}
            <div className="flex flex-1 flex-col overflow-hidden bg-[linear-gradient(180deg,rgba(10,12,18,0.72),rgba(8,10,16,0.82))]">
              {/* Tabs - renders immediately (static nav) */}
              <div className="mx-4 mt-4 flex w-auto items-center gap-1.5 rounded-[18px] border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.012))] p-1.5 sm:mx-6 sm:mt-5 sm:gap-2 flex-shrink-0">
                {/* Left side tabs */}
                <div className="min-w-0 flex-1 overflow-x-auto scrollbar-hide">
                  <div className="flex min-w-max items-center gap-1.5 pr-1 sm:gap-2 sm:pr-2">
                  {LEAD_DETAIL_TABS.map((tab) => {
                    const isActive = activeTab === tab.id;
                    const hasAI = 'hasAI' in tab && tab.hasAI;

                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        role="tab"
                        aria-selected={isActive}
                        aria-label={`${tab.label} tab${hasAI ? ' (AI-powered)' : ''}`}
                        className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[12px] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 sm:gap-2 sm:px-3.5
                        ${isActive
                            ? 'border border-white/10 bg-white/[0.08] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'
                            : 'border border-transparent text-[#A0A0A0] hover:bg-white/[0.04] hover:text-white'
                          }`}
                      >
                        {tab.label}
                        {hasAI && (
                          <span className="rounded-md border border-[#5260FF]/30 bg-[#5260FF]/20 px-1.5 py-0.5 text-[9px] font-black tracking-[0.12em] text-[#8EA4FF]" aria-label="AI">
                            AI
                          </span>
                        )}
                      </button>
                    );
                  })}
                  </div>
                </div>

                {/* Right side - Interactions button (replaces ISR Briefing).
                    Same accent style so users find the activity/interactions
                    surface easily. */}
                <button
                  onClick={() => setActiveTab('interactions')}
                  role="tab"
                  aria-selected={activeTab === 'interactions'}
                  aria-label="Interactions and activity tab"
                  className={`relative inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-[12px] border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] transition-all duration-300
                  ${activeTab === 'interactions'
                      ? 'border-cyan-200/60 bg-cyan-300/[0.10] text-white shadow-[0_0_0_1px_rgba(103,232,249,0.14),0_0_24px_rgba(34,211,238,0.22),inset_0_1px_0_rgba(255,255,255,0.09)] ring-1 ring-cyan-200/35'
                      : 'border-white/8 bg-white/[0.035] text-white/70 hover:border-white/14 hover:bg-white/[0.06] hover:text-white'
                    }`}
                >
                  {activeTab === 'interactions' && (
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-200 shadow-[0_0_12px_rgba(103,232,249,0.9)]" aria-hidden="true" />
                  )}
                  Interactions
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4 lg:px-6" data-view-block>
                {hydrating ? (
                  <div className="divide-y divide-white/[0.06]" aria-busy="true" aria-label="Loading lead content">
                    <SkeletonSection lines={4} />
                    <SkeletonSection lines={3} />
                    <SkeletonSection lines={5} />
                    <SkeletonSection lines={3} />
                  </div>
                ) : (
                  <div className="lead-detail-density animate-fadeIn">
                    {renderTabContent(displayLead)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
